import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MessageCircle, Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

export const Visita = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    purpose: 'visita',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Frontend-only submission - save to localStorage
    setTimeout(() => {
      const submissions = JSON.parse(localStorage.getItem('elcercado_submissions') || '[]');
      submissions.push({
        ...formData,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('elcercado_submissions', JSON.stringify(submissions));

      toast.success('¡Mensaje enviado!', {
        description: 'Te contactaremos pronto para coordinar tu visita.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        purpose: 'visita',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola, me gustaría ${formData.purpose === 'visita' ? 'visitar El Cercado' : 'tomar clases de alfarería'}.`
    );
    // WhatsApp number to be added by user later
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  return (
    <section id="visita" className="section-padding bg-cal-darker">
      <div className="editorial-container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="nav-label text-siena">Visita y Clases</span>
          <h2 className="display-heading text-4xl md:text-5xl lg:text-6xl text-earth mt-4 mb-6">
            Ven a conocer
            <br />
            <span className="text-ochre">la tradición de cerca</span>
          </h2>
          <p className="body-text text-lg">
            El Cercado te espera. Visita el taller, conoce a los artesanos
            o simplemente déjate envolver por quinientos años de historia viva.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-earth font-medium">
                  Nombre completo
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-cal-darker border-stone-light focus:border-siena focus:ring-siena"
                  placeholder="Tu nombre"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-earth font-medium">
                    Correo electrónico
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-cal-darker border-stone-light focus:border-siena focus:ring-siena"
                    placeholder="tu@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-earth font-medium">
                    Teléfono (opcional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-cal-darker border-stone-light focus:border-siena focus:ring-siena"
                    placeholder="+58..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="purpose" className="text-earth font-medium">
                  ¿Qué te interesa?
                </Label>
                <select
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-cal-darker border border-stone-light rounded-md focus:border-siena focus:ring-2 focus:ring-siena/20 transition-colors text-earth"
                >
                  <option value="visita">Visitar el taller</option>
                  <option value="clase">Tomar clases de alfarería</option>
                  <option value="compra">Adquirir piezas</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-earth font-medium">
                  Mensaje
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-cal-darker border-stone-light focus:border-siena focus:ring-siena resize-none"
                  placeholder="Cuéntanos un poco más sobre tu interés..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-siena hover:bg-siena-dark text-white font-sans text-sm uppercase tracking-wider py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Mail className="w-4 h-4" />
                      Enviar mensaje
                    </span>
                  )}
                </Button>

                <Button
                  type="button"
                  onClick={handleWhatsApp}
                  className="flex-1 bg-caribbean hover:bg-caribbean-light text-white font-sans text-sm uppercase tracking-wider py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <span className="flex items-center justify-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Escríbenos por WhatsApp
                  </span>
                </Button>
              </div>
            </form>
          </div>

          {/* Contact Info & Image */}
          <div className="space-y-8">
            {/* Image - Casa de El Cercado (debajo de WhatsApp) - Aclarada */}
            <div className="relative h-64 lg:h-80 rounded-lg overflow-hidden shadow-xl">
              <img
                src="/images/casa_cercado.jpg"
                alt="Casa tradicional de El Cercado"
                className="w-full h-full object-cover"
                style={{ 
                  objectPosition: 'center center',
                  filter: 'brightness(1.1)' 
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth/30 to-transparent" />
            </div>

            {/* Info Cards */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-6 bg-cal-darker rounded-lg">
                <div className="w-10 h-10 rounded-full bg-siena/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-siena" />
                </div>
                <div>
                  <h4 className="font-medium text-earth mb-1">Ubicación</h4>
                  <p className="text-sm text-stone leading-relaxed">
                    El Cercado, Municipio Gómez
                    <br />
                    Isla de Margarita, Nueva Esparta
                    <br />
                    Venezuela
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-cal-darker rounded-lg">
                <div className="w-10 h-10 rounded-full bg-ochre/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-ochre" />
                </div>
                <div>
                  <h4 className="font-medium text-earth mb-1">Horario</h4>
                  <p className="text-sm text-stone leading-relaxed">
                    Lunes a Sábado: 8:00 AM - 5:00 PM
                    <br />
                    Domingo: Solo con cita previa
                  </p>
                </div>
              </div>

              <div className="p-6 bg-caribbean/5 border border-caribbean/20 rounded-lg">
                <p className="text-sm text-earth-soft leading-relaxed">
                  <strong className="text-earth">Nota:</strong> El Cercado está a 40 minutos
                  en carro desde Porlamar. Te recomendamos contactarnos antes de tu visita
                  para confirmar la disponibilidad de los artesanos.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mapa */}
        <div className="mt-16 lg:mt-20">
          <div className="text-center mb-8">
            <span className="nav-label text-siena">Cómo llegar</span>
            <h3 className="display-heading text-3xl md:text-4xl text-earth mt-3">
              Encuéntranos en el mapa
            </h3>
          </div>
          <div className="rounded-lg overflow-hidden shadow-xl border border-stone-light">
            <iframe
              title="El Cercado, Isla de Margarita"
              src="https://maps.google.com/maps?q=El%20Cercado%2C%20Municipio%20G%C3%B3mez%2C%20Isla%20de%20Margarita%2C%20Venezuela&t=&z=14&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="450"
              style={{ border: 0, display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-iframe"
            />
          </div>
          <p className="text-center text-sm text-stone mt-4">
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=El+Cercado,+Municipio+G%C3%B3mez,+Isla+de+Margarita,+Venezuela"
              target="_blank"
              rel="noopener noreferrer"
              className="text-siena hover:text-siena-dark transition-colors underline underline-offset-4"
              data-testid="map-directions-link"
            >
              Abrir indicaciones en Google Maps →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
